// src/pages/Workflows/components/ExecutarModal/ExecutarModal.js
// Modal de execução em tempo real: formulário de inputs + log SSE + resultado

import React, { useState, useEffect } from 'react';
import { NODE_DEFINITIONS } from '../../Editor/utils/nodeDefinitions';
import { useExecutarWorkflow } from './useExecutarWorkflow';
import './ExecutarModal.css';

export default function ExecutarModal({ workflow, onFechar, onConcluido }) {
  const {
    etapa,
    nosComInput,
    formValues,
    files,
    logNos,
    estadoFinal,
    erro,
    execucaoId,
    setFormCampo,
    setFicheirosNo,
    iniciarExecucao,
    resetar,
  } = useExecutarWorkflow(workflow);

  const [casos, setCasos] = useState([]);
  const [loadingCasos, setLoadingCasos] = useState(false);

  // Primitivo estável derivado de nosComInput — boolean não cria nova referência a cada render
  const temCasoAnalisado = nosComInput.some(
    no =>
      no.data?.nodeType === 'formulario' &&
      (no.data?.config?.campos || []).some(c => c.tipo === 'caso_analisado')
  );

  useEffect(() => {
    if (!temCasoAnalisado) return;

    setLoadingCasos(true);
    const token = localStorage.getItem('token');
    fetch('/api/casos/todos?limite=100&pagina=1', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) setCasos(data.data?.casos || data.data || []);
      })
      .catch(() => {})
      .finally(() => setLoadingCasos(false));
  }, [temCasoAnalisado, workflow?.id]);

  const handleCasoChange = async (noId, campoNome, casoId) => {
    if (!casoId) { setFormCampo(noId, campoNome, null); return; }
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`/api/casos/${casoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (data.success) setFormCampo(noId, campoNome, data.data);
    } catch (_) {}
  };

  const handleFechar = () => {
    resetar();
    if (etapa === 'concluido' && execucaoId && onConcluido) {
      onConcluido(execucaoId);
    } else {
      onFechar();
    }
  };

  return (
    <div className="exec-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleFechar(); }}>
      <div className="exec-modal">
        {/* Header */}
        <div className="exec-header">
          <h2 className="exec-title">▶ Executar: {workflow?.nome}</h2>
          <button className="exec-close" onClick={handleFechar}>×</button>
        </div>

        {/* Corpo */}
        <div className="exec-body">
          {/* ── Etapa: Formulário ─────────────────────────────────── */}
          {etapa === 'formulario' && (
            <div className="exec-form-section">
              {nosComInput.length === 0 ? (
                <p className="exec-no-inputs">Este workflow não requer inputs. Clique em Executar para iniciar.</p>
              ) : (
                nosComInput.map(no => {
                  const tipo = no.data?.nodeType;
                  const def = NODE_DEFINITIONS[tipo];
                  const config = no.data?.config || {};

                  return (
                    <div key={no.id} className="exec-no-section">
                      <div className="exec-no-label">
                        <span className="exec-no-tipo-badge">{def?.label || tipo}</span>
                      </div>

                      {/* Formulário: campos configurados no nó */}
                      {tipo === 'formulario' && (config.campos || []).map(campo => (
                        <div key={campo.nome} className="exec-campo">
                          <label className="exec-campo-label">
                            {campo.label || campo.nome}
                            {campo.obrigatorio && <span style={{ color: '#ef4444' }}> *</span>}
                          </label>
                          {campo.tipo === 'caso_analisado' ? (
                            <select
                              className="exec-input"
                              value={(formValues[no.id] || {})[campo.nome]?.id || ''}
                              onChange={e => handleCasoChange(no.id, campo.nome, e.target.value)}
                            >
                              <option value="">
                                {loadingCasos ? 'A carregar casos...' : '-- Selecionar caso --'}
                              </option>
                              {casos.map(caso => (
                                <option key={caso.id} value={caso.id}>
                                  {caso.name || caso.titulo || `Caso ${caso.id}`}
                                </option>
                              ))}
                            </select>
                          ) : campo.tipo === 'textarea' ? (
                            <textarea
                              className="exec-input"
                              rows={4}
                              value={(formValues[no.id] || {})[campo.nome] || ''}
                              onChange={e => setFormCampo(no.id, campo.nome, e.target.value)}
                            />
                          ) : (
                            <input
                              className="exec-input"
                              type={campo.tipo || 'text'}
                              value={(formValues[no.id] || {})[campo.nome] || ''}
                              onChange={e => setFormCampo(no.id, campo.nome, e.target.value)}
                            />
                          )}
                        </div>
                      ))}

                      {/* Upload de ficheiros */}
                      {tipo === 'upload_ficheiros' && (
                        <div className="exec-campo">
                          <label className="exec-campo-label">Ficheiros</label>
                          <input
                            type="file"
                            className="exec-file-input"
                            multiple
                            onChange={e => setFicheirosNo(no.id, e.target.files)}
                          />
                          {files[no.id] && files[no.id].length > 0 && (
                            <div className="exec-file-count">
                              {files[no.id].length} ficheiro(s) selecionado(s)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}

              <div className="exec-actions">
                <button className="wf-btn wf-btn-secondary" onClick={handleFechar}>Cancelar</button>
                <button className="wf-btn wf-btn-primary" onClick={iniciarExecucao}>
                  ▶ Executar
                </button>
              </div>
            </div>
          )}

          {/* ── Etapa: Executando ─────────────────────────────────── */}
          {etapa === 'executando' && (
            <div className="exec-log-section">
              <p className="exec-log-title">A executar workflow...</p>
              <div className="exec-log-list">
                {logNos.length === 0 && (
                  <div className="exec-log-waiting">
                    <span className="exec-spinner" /> A iniciar...
                  </div>
                )}
                {logNos.map(no => (
                  <LogNoItem key={no.no_id} no={no} />
                ))}
              </div>
            </div>
          )}

          {/* ── Etapa: Concluído ──────────────────────────────────── */}
          {etapa === 'concluido' && (
            <div className="exec-resultado-section">
              {erro ? (
                <div className="exec-error-banner">
                  <span>❌ Erro: {erro}</span>
                </div>
              ) : (
                <div className={`exec-estado-banner exec-estado-${estadoFinal}`}>
                  {estadoFinal === 'completed' ? '✅ Workflow concluído com sucesso!' : '❌ Workflow falhou'}
                </div>
              )}

              {/* Log resumo */}
              <div className="exec-log-list exec-log-list--final">
                {logNos.map(no => (
                  <LogNoItem key={no.no_id} no={no} />
                ))}
              </div>

              <div className="exec-actions">
                <button className="wf-btn wf-btn-primary" onClick={handleFechar}>
                  {execucaoId ? 'Ver Resultados' : 'Fechar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LogNoItem({ no }) {
  const [expandido, setExpandido] = React.useState(false);

  const icone = {
    pending: '⏳',
    running: 'spinner',
    completed: '✅',
    failed: '❌',
    skipped: '⏭',
    skipped_preflight: '📋',
  }[no.estado] || '❓';

  const def = NODE_DEFINITIONS[no.no_tipo];

  return (
    <div className={`exec-log-item exec-log-item--${no.estado === 'skipped_preflight' ? 'completed' : no.estado}`}>
      <div className="exec-log-item-header" onClick={() => no.output_preview && setExpandido(!expandido)}>
        <span className="exec-log-icone">
          {icone === 'spinner' ? <span className="exec-spinner" /> : icone}
        </span>
        <span className="exec-log-nome">{def?.label || no.no_tipo}</span>
        {no.erro && <span className="exec-log-erro-inline">{no.erro}</span>}
        {no.output_preview && !no.erro && (
          <span className="exec-log-expand">{expandido ? '▲' : '▼'}</span>
        )}
      </div>
      {expandido && no.output_preview && (
        <div className="exec-log-output">
          <pre>{no.output_preview}</pre>
        </div>
      )}
    </div>
  );
}